import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Clock, MapPin, Pin } from 'lucide-react';
import UserAvatar from '../UserAvatar'; 
import { formatTimeAgo } from '../../../utils/formatTimeAgo';
import { generatePendencyId } from '../../../utils/pendencyUtils';
import { getPendencyObservationText } from '../../../utils/pendencyContent';
import { motion } from 'framer-motion';

/**
 * @description Card para exibir um resumo da pendência com visual aprimorado.
 * @param {object} props - Propriedades do componente.
 * @param {object} props.pendency - O objeto da pendência.
 * @param {function} props.onCardClick - Função a ser executada ao clicar no card.
 * @param {object} [props.creatorUser] - Objeto do usuário que criou a pendência.
 * @param {object} [props.assignedUser] - Objeto do usuário para quem a pendência foi atribuída.
 * @param {boolean} props.isPinned - Indica se o card está fixado.
 * @param {function} props.onPinClick - Função para fixar/desafixar o card.
 * @param {boolean} props.canPin - Indica se o usuário tem permissão para fixar.
 */
const PendencyCard = ({ pendency, onCardClick, creatorUser, assignedUser, isPinned, onPinClick, canPin }) => {
    const pendencyId = pendency.displayId || generatePendencyId(pendency.id);

    // --- Mapeamento de estilos para Prioridade ---
    const priorityClasses = {
        'Alta': 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300',
        'Média': 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300',
        'Baixa': 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300',
    };

    // --- Mapeamento de estilos para Status ---
    const statusClasses = {
        'Pendente': 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300',
        'Executado': 'bg-sky-100 text-sky-800 dark:bg-sky-900/50 dark:text-sky-300', 
    };

    // Calcula o tempo decorrido para usar na etiqueta combinada
    const timeAgo = formatTimeAgo(pendency.dataCadastro);
    const isExecuted = pendency.status === 'Executado';

    // --- Lógica da Etiqueta Combinada (Status + Tempo) ---
    const statusOrTimeLabel = isExecuted ? 'Executado' : timeAgo?.text;
    const statusOrTimeColorClass = isExecuted ? statusClasses['Executado'] : timeAgo?.className;

    const displayObservations = getPendencyObservationText(pendency);

    const handlePinClick = (e) => {
        e.stopPropagation();
        onPinClick(pendency.id);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onCardClick(pendency);
        }
    };

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 50, scale: 0.3 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5, transition: { duration: 0.2 } }}
            onClick={() => onCardClick(pendency)}
            onKeyDown={handleKeyDown}
            role="button"
            tabIndex={0}
            aria-label={`Pendência #${pendencyId}: ${pendency.descricao}`}
            className={`
                relative
                ${isPinned ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-400 dark:border-amber-500' : 'bg-slate-50 dark:bg-slate-800/60 border-slate-200 dark:border-slate-700'}
                p-4 rounded-lg border outline-none focus-visible:ring-2 focus-visible:ring-blue-500
                flex flex-col justify-between gap-3
                transition-colors duration-200 cursor-pointer
                hover:border-blue-500 dark:hover:border-blue-400
                ${isExecuted ? 'opacity-60 hover:opacity-100' : ''}
            `}
        >
            {canPin && (
                <button
                    onClick={handlePinClick}
                    className={`absolute top-2 right-2 p-1 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-blue-500
                        ${isPinned ? 'text-amber-600' : 'text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'}
                        transition-colors z-10`}
                    title={isPinned ? "Desafixar card" : "Fixar card"}
                    aria-label={isPinned ? "Desafixar card" : "Fixar card"}
                >
                    <Pin className={`h-5 w-5 ${isPinned ? 'fill-current' : ''}`} />
                </button>
            )}
            {/* Seção Superior: ID, Cidade e Descrição */}
            <div>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-md bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300">
                        #{pendencyId}
                    </span>
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-md bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300 flex items-center">
                        <MapPin className="h-3 w-3 mr-1.5" />
                        {pendency.cidade}
                    </span>
                </div>
                <p className="font-bold text-slate-800 dark:text-slate-100 truncate" title={pendency.descricao}>{pendency.descricao}</p>

                {/* Seção de Observações */}
                {displayObservations && (
                    <div className="mt-2 text-xs font-normal text-slate-600 dark:text-slate-400 max-h-16 overflow-hidden relative group">
                        <div className="prose prose-xs dark:prose-invert max-w-none leading-snug">
                            <ReactMarkdown
                                components={{
                                    p: ({node, ...props}) => <p className="m-0" {...props} />,
                                }}
                            >
                                {displayObservations}
                            </ReactMarkdown>
                        </div>
                        <div className="absolute bottom-0 left-0 w-full h-4 bg-gradient-to-t from-slate-50 dark:from-slate-800/60 to-transparent"></div>
                    </div>
                )}
            </div>

            {/* Seção Inferior: Tags, Data e Avatares */}
            <div className="flex items-center justify-between text-xs font-semibold mt-2">
                <div className="flex items-center gap-2 flex-wrap">
                    {/* Tag de Prioridade */}
                    {!isExecuted && (
                        <span className={`px-2.5 py-1 rounded-full ${priorityClasses[pendency.prioridade]}`}>
                            {pendency.prioridade}
                        </span>
                    )}
                    
                    {/* Etiqueta Combinada de Status e Tempo */}
                    {statusOrTimeLabel && (
                        <span className={`px-2.5 py-1 rounded-full flex items-center ${statusOrTimeColorClass}`}>
                            {!isExecuted && <Clock className="h-3 w-3 mr-1.5"/>}
                            {statusOrTimeLabel}
                        </span>
                    )}
                </div>
                
                <div className="flex items-center gap-2">
                    {/* Data de Cadastro */}
                    <span className="text-slate-400 dark:text-slate-500">
                        {new Date(pendency.dataCadastro).toLocaleDateString()}
                    </span>
                    {/* Avatares dos Usuários */}
                    <div className="flex items-center -space-x-3">
                        {assignedUser && <UserAvatar user={assignedUser} />}
                        {creatorUser && <UserAvatar user={creatorUser} />}
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

// Optimized: Wrapped in React.memo with custom comparison to prevent unnecessary re-renders
// when parent component (PendenciesPage) updates and creates new pendency object references.
const pendencyAreEqual = (prevProps, nextProps) => {
    if (prevProps.onCardClick !== nextProps.onCardClick) return false;
    if (prevProps.isPinned !== nextProps.isPinned) return false;
    if (prevProps.onPinClick !== nextProps.onPinClick) return false;
    if (prevProps.canPin !== nextProps.canPin) return false;

    const p1 = prevProps.pendency;
    const p2 = nextProps.pendency;
    if (!p1 || !p2) return p1 === p2;
    if (p1.id !== p2.id) return false;
    if (p1.updatedAt !== p2.updatedAt) return false;

    // Fallback comparison for rendered fields if updatedAt is not sufficient
    if (p1.status !== p2.status) return false;
    if (p1.prioridade !== p2.prioridade) return false;
    if (p1.cidade !== p2.cidade) return false;
    if (p1.descricao !== p2.descricao) return false;
    if (p1.dataCadastro !== p2.dataCadastro) return false;
    if (p1.observacoes !== p2.observacoes) return false;

    // Compara o primeiro comentário raiz
    const rootComments1 = p1.comments?.filter(c => !c.parentId) || [];
    const firstComment1 = rootComments1.length > 0
        ? [...rootComments1].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())[0]
        : null;

    const rootComments2 = p2.comments?.filter(c => !c.parentId) || [];
    const firstComment2 = rootComments2.length > 0
        ? [...rootComments2].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())[0]
        : null;

    if (firstComment1?.text !== firstComment2?.text) return false;
    if (firstComment1?.id !== firstComment2?.id) return false;

    // Assigned/Creator Users checking (UIDs & names for avatar updates)
    if (prevProps.creatorUser?.uid !== nextProps.creatorUser?.uid) return false;
    if (prevProps.creatorUser?.displayName !== nextProps.creatorUser?.displayName) return false;
    if (prevProps.creatorUser?.photoURL !== nextProps.creatorUser?.photoURL) return false;

    if (prevProps.assignedUser?.uid !== nextProps.assignedUser?.uid) return false;
    if (prevProps.assignedUser?.displayName !== nextProps.assignedUser?.displayName) return false;
    if (prevProps.assignedUser?.photoURL !== nextProps.assignedUser?.photoURL) return false;

    return true;
};

export default React.memo(PendencyCard, pendencyAreEqual);
